import { cn } from '@/client/lib/utils';

interface JsonSyntaxHighlightProps {
  json: string;
  className?: string;
}

/**
 * Escape HTML before it goes through dangerouslySetInnerHTML.
 * Payloads routinely carry markup inside string values (event descriptions,
 * WordPress post content), which would otherwise render as live HTML.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function JsonSyntaxHighlight({ json, className }: JsonSyntaxHighlightProps) {
  try {
    const parsed = JSON.parse(json);
    const formatted = escapeHtml(JSON.stringify(parsed, null, 2));

    // Apply syntax highlighting
    const highlighted = formatted
      .replace(/("(?:[^"\\]|\\.)*")(\s*:)/g, '<span class="text-blue-600 dark:text-blue-400">$1</span>$2') // keys
      .replace(/:\s*("(?:[^"\\]|\\.)*")/g, ': <span class="text-green-600 dark:text-green-400">$1</span>') // string values
      .replace(/:\s*(\d+\.?\d*)/g, ': <span class="text-amber-600 dark:text-amber-400">$1</span>') // numbers
      .replace(/:\s*(true|false)/g, ': <span class="text-purple-600 dark:text-purple-400">$1</span>') // booleans
      .replace(/:\s*(null)/g, ': <span class="text-gray-500">$1</span>'); // null

    return (
      <code
        className={cn('block whitespace-pre-wrap break-all', className)}
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    );
  } catch {
    // If not valid JSON, just return as-is
    return <code className={cn('block whitespace-pre-wrap break-all', className)}>{json}</code>;
  }
}
